import { isUnansweredRequest } from '../../api/unanswered-request';
import type { ResumeToken } from './protocol';

/**
 * Why an upload stopped. The kind decides whether it is retried and what the
 * user is told:
 *
 * - `cancelled`: the user stopped it. Never retried, never an error message.
 * - `stalled`: no bytes moved for the stall window.
 * - `network`: the request never got an answer (dropped connection, DNS…).
 * - `expired`: storage refused the signed URL (403); it is re-signed and retried.
 * - `http`: storage or the API answered with an error status.
 * - `too-large`: the server refused the size. Never retried.
 */
export type UploadErrorKind =
  | 'cancelled'
  | 'stalled'
  | 'network'
  | 'expired'
  | 'http'
  | 'too-large';

/** Who answered an `http` / `expired` failure: our API or the storage PUT. */
export type UploadErrorSource = 'api' | 'storage';

/**
 * What a failed upload was moving, for the error report: a storage 502 or a
 * stall says little alone, but "a 120 MB audio file, after 4 retries" names
 * which uploads fail (FLASHKA-VITE-2WR, FLASHKA-APP-1B9).
 */
export type UploadDiagnostics = {
  /** `uploadSizeBucket` of the file — a range, never the exact size. */
  sizeBucket: string;
  contentType: string;
  /** Failed requests the retry loop retried before giving up. */
  retries: number;
};

/** A file size as a coarse range for reports. */
export function uploadSizeBucket(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (!Number.isFinite(mb) || mb < 0) return 'unknown';
  if (mb < 1) return '<1MB';
  if (mb < 10) return '1-10MB';
  if (mb < 50) return '10-50MB';
  if (mb < 200) return '50-200MB';
  return '>=200MB';
}

export class UploadError extends Error {
  readonly kind: UploadErrorKind;
  readonly status?: number;
  readonly retryAfterMs?: number;
  readonly where?: UploadErrorSource;
  /**
   * Set only by `toUploadError` when it had to GUESS: an error no adapter
   * classified, with no status, filed as `network` so the retry loop and the
   * copy treat it as one. The original is kept so `isExpectedUploadError` can
   * tell a real transport failure from a bug that merely has no status.
   */
  readonly unclassified?: { error: unknown };
  /**
   * False when continuing the same upload would fail the same way: `uploadFile`
   * hands back no resume token, so "Try again" begins a new upload.
   */
  readonly resumable: boolean;
  /** Set on a failed multipart upload: pass it back to continue where it stopped. */
  resume?: ResumeToken;
  /** What the upload was moving, set by `uploadFile` as the error leaves it. */
  diagnostics?: UploadDiagnostics;

  constructor(
    kind: UploadErrorKind,
    message?: string,
    extra: {
      status?: number;
      retryAfterMs?: number;
      where?: UploadErrorSource;
      unclassified?: { error: unknown };
      resumable?: boolean;
    } = {},
  ) {
    super(message ?? `Upload ${kind}`);
    this.name = 'UploadError';
    this.kind = kind;
    this.status = extra.status;
    this.retryAfterMs = extra.retryAfterMs;
    this.where = extra.where;
    this.unclassified = extra.unclassified;
    this.resumable = extra.resumable ?? true;
  }
}

/** A `Retry-After` header (seconds or an HTTP date) as milliseconds. */
export function parseRetryAfter(
  header: string | null | undefined,
  now = Date.now(),
): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const at = Date.parse(header);
  return Number.isFinite(at) ? Math.max(0, at - now) : undefined;
}

/**
 * The error for a non-2xx answer. `where` says who answered: only STORAGE
 * refusing a signed PUT with 403 means the URL expired and re-signing helps.
 * A 403 from our own API (the key is not the caller's) will say the same
 * thing on every retry, so it fails at once.
 */
export function httpUploadError(
  status: number,
  retryAfter?: string | null,
  where: UploadErrorSource = 'api',
): UploadError {
  if (status === 403 && where === 'storage') {
    return new UploadError('expired', 'Signed URL refused', { status, where });
  }
  if (status === 413) {
    return new UploadError('too-large', 'File too large', { status, where });
  }
  // Who answered is in the message: a storage 502 (FLASHKA-VITE-1YV, R2's
  // gateway) and our API's 502 are different owners.
  return new UploadError('http', `Upload failed with status ${status} (${where})`, {
    status,
    retryAfterMs: parseRetryAfter(retryAfter),
    where,
  });
}

/**
 * How a reportable upload failure is filed in Sentry: one issue per
 * kind × who answered × status, whichever screen's call stack threw it —
 * "storage answered 502 after the retries" is one problem with one owner, not
 * one issue per upload surface. Tags carry the same facts for filtering.
 * `null` for anything that is not an `UploadError`.
 */
export function uploadErrorReportContext(
  error: unknown,
): { fingerprint: string[]; tags: Record<string, string> } | null {
  if (!(error instanceof UploadError)) return null;
  const where = error.where ?? 'unknown';
  const status = error.status === undefined ? 'none' : String(error.status);
  return {
    fingerprint: ['upload-error', error.kind, where, status],
    tags: { upload_kind: error.kind, upload_where: where, upload_status: status },
  };
}

/**
 * The multipart session no longer exists (404 / 410): R2 dropped it, it was
 * aborted, or storage is no longer R2. Resuming it cannot work — the upload
 * has to begin again.
 */
export function isUploadSessionGone(error: unknown): boolean {
  return (
    error instanceof UploadError &&
    error.kind === 'http' &&
    (error.status === 404 || error.status === 410)
  );
}

export function isRetryableUploadError(error: UploadError): boolean {
  switch (error.kind) {
    case 'stalled':
    case 'network':
    case 'expired':
      return true;
    case 'http':
      return (
        error.status === undefined ||
        error.status === 408 ||
        error.status === 429 ||
        error.status >= 500
      );
    case 'cancelled':
    case 'too-large':
      return false;
  }
}

/**
 * A failure the USER owns — they stopped it, their connection dropped or
 * stalled, or the file is over the size limit — rather than a bug of ours.
 * Both clients tell the user either way (the upload toast with Retry); only
 * the rest is worth an error report. Reporting these was FLASHKA-APP-1TP /
 * -1TQ on mobile and FLASHKA-VITE-2V9, -2VE, -2VK, -2VX on web.
 *
 * STILL REPORTABLE: `http` (storage or our API answered with an error, a 5xx
 * after the retries ran out), `expired` (a refused re-sign), and a `network`
 * that `toUploadError` guessed for an error that was not a lost request.
 */
export function isExpectedUploadError(error: unknown): boolean {
  if (!(error instanceof UploadError)) return false;
  // A `network` that `toUploadError` guessed is expected only when the original
  // really was a lost request. Anything else with no status — a TypeError in an
  // adapter, a body that would not parse, an empty signed URL the transport
  // refused — is a bug wearing the `network` label, and stays reportable.
  if (error.kind === 'network' && error.unclassified) {
    return isUnansweredRequest(error.unclassified.error);
  }
  return (
    error.kind === 'cancelled' ||
    error.kind === 'network' ||
    error.kind === 'stalled' ||
    error.kind === 'too-large'
  );
}

/**
 * Normalise whatever a platform adapter threw. Adapters should throw
 * `UploadError`, but an axios / fetch error with a status is read as an HTTP
 * answer and anything else as a network failure.
 */
export function toUploadError(error: unknown): UploadError {
  if (error instanceof UploadError) return error;
  const status =
    (error as { status?: unknown })?.status ??
    (error as { response?: { status?: unknown } })?.response?.status;
  if (typeof status === 'number') return httpUploadError(status);
  const message = error instanceof Error ? error.message : String(error);
  return new UploadError('network', message, { unclassified: { error } });
}

/**
 * An XHR `onerror`: it carries no detail at all, so offline, a storage CORS
 * rule that stopped matching, DNS and TLS all look the same. Expected only
 * when the browser says it is offline as it fails; otherwise still `network`
 * for the retry loop and the copy, but kept reportable (`unclassified`) so a
 * broken bucket is not filed as everyone's wifi.
 */
export function xhrNetworkUploadError(message: string): UploadError {
  const offline =
    typeof navigator !== 'undefined' &&
    (navigator as { onLine?: unknown }).onLine === false;
  return new UploadError(
    'network',
    message,
    offline ? {} : { unclassified: { error: new Error(message) } },
  );
}
